import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { BudgetDto, BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetLineParameterDto } from 'api/budgets/models/BudgetLineParameterDto';
import { AtLeast } from 'common/types/Atleast';

export enum AddLinePlacement {
    SAME,
    INSIDE,
    ROOT,
}

export const SET_SELECTED_LINE_KEYS = 'SET_SELECTED_LINE_KEYS';
export const TOGGLE_SELECTED_LINE_KEY = 'TOGGLE_SELECTED_LINE_KEY';
export const SELECT_RANGE_TO = 'SELECT_RANGE_TO';
export const SET_LINES = 'SET_LINES';
export const SET_BUDGET = 'SET_BUDGET';
export const ADD_LINE = 'ADD_LINE';
export const COPY_LINES = 'COPY_LINES';
export const CUT_LINES = 'CUT_LINES';
export const PASTE_LINES = 'PASTE_LINES';
export const HYDRATE_CLIPBOARD = 'HYDRATE_CLIPBOARD';
export const UPDATE_LINE = 'UPDATE_LINE';
export const REMOVE_LINES = 'REMOVE_LINES';
export const REMOVE_LINE_CHILDREN = 'REMOVE_LINE_CHILDREN';
export const CLONE_LINE = 'CLONE_LINE';
export const CALCULATE_LINE_TOTALS = 'CALCULATE_LINE_TOTALS';
export const CALCULATE_ALL_LINES_TOTALS = 'CALCULATE_ALL_LINES_TOTALS';
export const CALCULATE_BUDGET_TOTALS = 'CALCULATE_BUDGET_TOTALS';
export const UPDATE_BUDGET = 'UPDATE_BUDGET';
export const UPDATE_LINE_QUANTITY = 'UPDATE_LINE_QUANTITY';
export const UPDATE_ALL_LINES = 'UPDATE_ALL_LINES';
export const UPDATE_LINE_PARAMETER = 'UPDATE_LINE_PARAMETER';
export const UPDATE_LINES_NUMBERS = 'UPDATE_LINES_NUMBERS';
export const MOVE_LINES_UP = 'MOVE_LINES_UP';
export const MOVE_LINES_DOWN = 'MOVE_LINES_DOWN';
export const SET_OPEN_LINES_INDEXES = 'SET_OPEN_LINES_INDEXES'
export const UPDATE_LINES_EXPRESSIONS_VALUES = 'UPDATE_LINES_EXPRESSIONS_VALUES'
export const SET_CALCULATING = 'SET_CALCULATING';
export const RECALCULATE_LINE_PARAMETER = 'RECALCULATE_LINE_PARAMETER';

export type Action = SetSelectedLineKeysAction
| ToggleSelectedLineKeyAction
| SelectRangeToAction
| SetLinesAction
| SetBudgetAction
| UpdateLineAction
| AddLineAction
| CloneLineAction
| CopyLinesAction
| CutLinesAction
| PasteLinesAction
| HydrateClipboardAction
| RemoveLinesAction
| CaclulateLineTotalsAction
| CaclulateAllLinesTotalsAction
| CaclulateBudgetTotalsAction
| UpdateBudgetAction
| UpdateLineQuantityAction
| UpdateAllLinesAction
| UpdateLineParameterAction
| UpdateLinesNumbersAction
| MoveLinesUpAction
| MoveLinesDownAction
| SetOpenLinesIndexesAction
| RemoveLineChildrenAction
| UpdateLinesExpressionsValuesAction
| SetCalculatingAction
| RecalculateLineParameterAction;

export interface SetSelectedLineKeysAction {
    type: typeof SET_SELECTED_LINE_KEYS;
    selectedLineKeys: string[];
    selectionAnchorKey: string | null;
}

export interface ToggleSelectedLineKeyAction {
    type: typeof TOGGLE_SELECTED_LINE_KEY;
    lineKey: string;
}

export interface SelectRangeToAction {
    type: typeof SELECT_RANGE_TO;
    lineKey: string;
}

export interface SetLinesAction {
    type: typeof SET_LINES;
    lines: BudgetLineDto[];
}

export interface SetBudgetAction {
    type: typeof SET_BUDGET;
    budget: BudgetData;
}

export interface UpdateLineAction {
    type: typeof UPDATE_LINE;
    line: AtLeast<BudgetLineDto, 'key'>;
}

export interface AddLineAction {
    type: typeof ADD_LINE;
    lineType: BudgetLineType;
    placement: AddLinePlacement;
    lineKey: string | null;
    newLineKey: string | null;
}

export interface RemoveLinesAction {
    type: typeof REMOVE_LINES;
    lineKeys: string[];
}

export interface RemoveLineChildrenAction {
    type: typeof REMOVE_LINE_CHILDREN;
    parentLineKey: string;
}

export interface UpdateLinesExpressionsValuesAction {
    type: typeof UPDATE_LINES_EXPRESSIONS_VALUES;
}

export interface CloneLineAction {
    type: typeof CLONE_LINE;
    lineKey: string;
    newLineKey: string;
}

export interface CopyLinesAction {
    type: typeof COPY_LINES;
    lineKeys: string[];
}

export interface CutLinesAction {
    type: typeof CUT_LINES;
    lineKeys: string[];
}

export interface PasteLinesAction {
    type: typeof PASTE_LINES;
    targetLineKey: string;
    newKeysByOldKey: Record<string, string>;
}

export interface HydrateClipboardAction {
    type: typeof HYDRATE_CLIPBOARD;
    copiedLineKeys: string[];
    copyType: CopyType;
    tempLines: BudgetLineDto[];
}

export interface UpdateLineQuantityAction {
    type: typeof UPDATE_LINE_QUANTITY;
    lineKey: string;
}

export interface CaclulateLineTotalsAction {
    type: typeof CALCULATE_LINE_TOTALS;
    lineKey: string;
}

export interface CaclulateAllLinesTotalsAction {
    type: typeof CALCULATE_ALL_LINES_TOTALS;
}

export interface CaclulateBudgetTotalsAction {
    type: typeof CALCULATE_BUDGET_TOTALS;
}

export interface UpdateBudgetAction {
    type: typeof UPDATE_BUDGET;
    budget: Partial<BudgetData>;
}

export interface UpdateAllLinesAction {
    type: typeof UPDATE_ALL_LINES;
    line: Partial<BudgetLineDto>;
}

export interface UpdateLineParameterAction {
    type: typeof UPDATE_LINE_PARAMETER;
    lineKey: string;
    parameter: AtLeast<BudgetLineParameterDto, 'key'>;
}

export interface UpdateLinesNumbersAction {
    type: typeof UPDATE_LINES_NUMBERS;
}

export interface MoveLinesUpAction {
    type: typeof MOVE_LINES_UP;
    lineKeys: string[];
}

export interface MoveLinesDownAction {
    type: typeof MOVE_LINES_DOWN;
    lineKeys: string[];
}

export interface SetOpenLinesIndexesAction {
    type: typeof SET_OPEN_LINES_INDEXES;
    openLinesIndexes: number[];
}

export interface SetCalculatingAction {
    type: typeof SET_CALCULATING;
    calculating: boolean;
}

export interface RecalculateLineParameterAction {
    type: typeof RECALCULATE_LINE_PARAMETER;
    lineKey: string;
    changedFamilyParameterId?: string;
}

export type CopyType = 'copy' | 'cut' | null;

export interface BudgetState {
    selectedLineKeys: string[];
    selectionAnchorKey: string | null;
    copiedLineKeys: string[];
    copyType: CopyType;
    lines: BudgetLineDto[];
    tempLines: BudgetLineDto[];
    budget: BudgetData;
    openLinesIndexes: number[];
    calculating: boolean;
}

export type BudgetData = Pick<BudgetDto,
'defaultTaxId' |
'totalNetExcludingTax' |
'totalTax' |
'totalIncludingTax' |
'totalExcludingTax' |
'discount' |
'discountTaxId' |
'discountTaxValue' |
'defaultTaxValue' |
'decimalPlaces' |
'defaultDiscountPercentage' |
'defaultMarginPercentage' |
'defaultCommissionMarginPercentage' |
'defaultDecennialInsurancePercentage' |
'decennialInsuranceExpression' |
'commissionExpression' |
'configs' |
'languageId'
> & {
    // Aggregated generality subtotals (kept in sync from the generalities form tab).
    // Folded into the canonical totals above so the footer / header / saved budget
    // reflect generalities in addition to the budget lines.
    generalitiesTotalExcludingTax?: number;
    generalitiesTotalTax?: number;
    // Lines-only totals (without generalities). These are what the expression engine
    // (line + generality expressions) references, preserving the historical meaning of
    // "budget total" and preventing self-referential generality expressions from looping.
    linesTotalNetExcludingTax?: number;
    linesTotalExcludingTax?: number;
    linesTotalTax?: number;
    linesTotalIncludingTax?: number;
}
